import { useLayoutEffect, type RefObject } from 'react'
import { timeCursor } from '../../app/time/cursor'
import type { V5Data, V5Frame } from '../data/types'

export function frameAtCursor(data: V5Data): V5Frame | null {
  return timeCursor.isNow() ? data.now : data.frames[data.models?.indexAt(timeCursor.instant()) ?? 0] ?? data.now
}

/** Read nodes once at bind time. Returned callback must perform bounded writes only. */
export function useFrameBinding<T extends HTMLElement>(ref: RefObject<T | null>, data: V5Data, bind: (node: T) => (frame: V5Frame) => void): void {
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const apply = bind(node)
    const update = () => { const frame = frameAtCursor(data); if (frame) apply(frame) }
    update()
    if (typeof IntersectionObserver === 'undefined') return timeCursor.subscribe(update)
    let unsubscribe = timeCursor.subscribe(update)
    // Prepare just before a section enters the viewport; offscreen sections do no cursor work.
    const observer = new IntersectionObserver(([entry]) => {
      unsubscribe()
      unsubscribe = () => {}
      if (entry.isIntersecting) { update(); unsubscribe = timeCursor.subscribe(update) }
    }, { rootMargin: '200px 0px' })
    observer.observe(node)
    return () => { observer.disconnect(); unsubscribe() }
  }, [ref, data, bind])
}

export function writeText(node: Element | null, value: string): void {
  if (!node) return
  const child = node.firstChild
  if (child?.nodeType === 3 && child.nextSibling == null) {
    // Keep the bound text node (and React's reference) instead of replacing the child list.
    const text = child as Text
    if (text.data !== value) text.data = value
  } else if (node.textContent !== value) node.textContent = value
}
